import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button, Card, Input, Label } from '@features/ui';
import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';

const schema = yup.object({
  password: yup
    .string()
    .matches(/^(?=.*[a-z])/, 'Must contain at least one lowercase letter')
    .matches(/^(?=.*[A-Z])/, 'Must contain at least one uppercase letter')
    .matches(/^(?=.*\d)/, 'Must contain at least one number')
    .matches(
      /^(?=.*[@$!%*?&^#()\-+=_{}[\]:;<>.,/\\|`~])/,
      'Must contain at least one special character'
    )
    .matches(/^[^\s]+$/, 'Must not contain spaces')
    .min(8, 'Password must be at least 8 characters long')
    .max(16, 'Password must be at most 16 characters long')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .test('passwords-match', 'Passwords must match', function (value) {
      return this.parent.password === value;
    }),
});

export type ResetPasswordFormData = yup.InferType<typeof schema>;

type Props = {
  className?: string;
  onSubmit: (data: ResetPasswordFormData) => void;
};

export const ResetPasswordForm: React.FC<Props> = ({ className, onSubmit }) => {
  const { t } = useTranslation();
  const passwordRules = useMemo(
    () => [
      'common.form.passwordRules.minLength',
      'common.form.passwordRules.upperCase',
      'common.form.passwordRules.lowerCase',
      'common.form.passwordRules.number',
      'common.form.passwordRules.specialChar',
      'common.form.passwordRules.noSpace',
    ],
    []
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      password: '',
    },
  });

  return (
    <form
      autoComplete="off"
      className="w-full lg:max-w-[584px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Card
        className={`py-8 pr-8 pl-8 xl:py-12.5 xl:pl-23.25 xl:pr-21.75 ${className ?? ''}`}
      >
        <h3 className="text-center text-[27px] mb-5 font-medium">
          {t('common.form.resetPassword')}
        </h3>
        <p className="text-center mb-7.5 font-medium">
          {t('resetPassword.form.description')}
        </p>
        <Controller
          control={control}
          name="password"
          render={({ field }) => (
            <div className="flex flex-col gap-y-2.5 mb-5">
              <Label htmlFor={field.name}>{t('common.form.password')}</Label>
              <div>
                <Input
                  {...field}
                  error={!!errors.password}
                  id={field.name}
                  inputSize={'lg'}
                  placeholder={t('common.form.password')}
                  type="password"
                />
              </div>
            </div>
          )}
        />
        <ul className="list-disc pl-6 mb-5">
          {passwordRules.map((rule) => (
            <li key={rule} className="font-medium">
              {t(rule)}
            </li>
          ))}
        </ul>
        <Controller
          control={control}
          name="confirmPassword"
          render={({ field }) => (
            <div className="flex flex-col gap-y-2.5 mb-5">
              <Label htmlFor={field.name}>
                {t('common.form.confirmPassword')}
              </Label>
              <div>
                <Input
                  {...field}
                  error={!!errors.confirmPassword}
                  id={field.name}
                  inputSize={'lg'}
                  placeholder={t('common.form.confirmPassword')}
                  type="password"
                />
                {errors.confirmPassword && (
                  <p className="text-destructive text-xs mt-1">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>
          )}
        />
        <Button
          className="w-full font-bold mb-5"
          loading={isSubmitting}
          size="lg"
          type="submit"
          variant="gradient"
        >
          {t('common.form.resetPassword')}
        </Button>
      </Card>
    </form>
  );
};
